import { datadogRum } from "@datadog/browser-rum";
import { liteClient as algoliasearch, type LiteClient } from "algoliasearch/lite";

import {
  databaseCatalogClient,
  toFallbackRecords,
  withDatabaseFallback,
} from "@/utils/algoliaFallback";
import { withCatalogSearchWhen } from "@/utils/catalogSearchClient";

let cachedClient: LiteClient | null = null;

/**
 * Whether this practice's shop reads the in-house catalog search instead of Algolia (or, in a build without an
 * index, instead of the in-browser database answerer). Off by default; turned on per practice in GrowthBook while
 * the in-house search rolls out, and off again to roll back without a deploy.
 */
const isInHouseCatalogSearchOn = async () => {
  const { getFeatureFlag } = await import("services/Flags");
  return getFeatureFlag("inHouseCatalogSearch");
};

/** The database catalog the fallback answers from; each source is cached like every other read of it. */
export const loadFallbackRecords = async () => {
  const [{ queryClient }, api, { STALE_TIME }] = await Promise.all([
    import("pages/Root"),
    import("api/methods"),
    import("api/staleTimes"),
  ]);
  const platform = await queryClient.fetchQuery({
    ...api.getPlatform.keyed(),
    staleTime: STALE_TIME.platformConfig,
  });
  const [listings, shop, customCategories, memberships, giftCards] = await Promise.all([
    queryClient.fetchQuery({ ...api.getListings.keyed(), staleTime: STALE_TIME.catalog }),
    queryClient.fetchQuery({ ...api.getShop.keyed() }),
    queryClient.fetchQuery({
      ...api.getShopCustomCategoriesListings.keyed(),
      staleTime: 1000 * 10,
    }),
    queryClient.fetchQuery({ ...api.getMemberships.keyed() }),
    platform.platformSettings?.giftCardEnabled
      ? queryClient.fetchQuery({ ...api.getGiftCards.keyed({ FilterActiveOnly: true }) })
      : [],
  ]);

  return toFallbackRecords({
    platformId: platform.id,
    listings,
    concerns: shop.concerns ?? [],
    customCategories,
    memberships,
    giftCards,
  });
};

/**
 * Index names in a build with no Algolia index. The database answerer still reads the replica suffix
 * (`_price_asc`, `_best_selling`, ...) to pick the sort.
 */
const DATABASE_CATALOG_ENV_NAME = "database";

/**
 * Whether this build reads the catalog from an Algolia index. A build without `REACT_APP_ALGOLIA_ENV` (a snapshot
 * without the `snap-algolia` label, or a local stack) has no index holding its own database's listings, so shop,
 * search and rails read the database instead — Algolia would list another environment's catalog.
 */
export const isAlgoliaCatalog = (): boolean => !!import.meta.env.REACT_APP_ALGOLIA_ENV;

export function getAlgoliaSearchClient(): LiteClient {
  if (cachedClient) {
    return cachedClient;
  }

  if (!isAlgoliaCatalog()) {
    cachedClient = withCatalogSearchWhen(
      databaseCatalogClient(loadFallbackRecords),
      isInHouseCatalogSearchOn,
    ) as unknown as LiteClient;
    return cachedClient;
  }

  const appId = import.meta.env.REACT_APP_ALGOLIA_APP_ID;
  const searchApiKey = import.meta.env.REACT_APP_ALGOLIA_SEARCH_API_KEY;

  if (!appId) {
    throw new Error(
      "Missing REACT_APP_ALGOLIA_APP_ID. Copy .env.sample to .env in Patient/RepeatMD.PatientFrontend and set Algolia credentials from 1Password.",
    );
  }

  if (!searchApiKey) {
    throw new Error(
      "Missing REACT_APP_ALGOLIA_SEARCH_API_KEY. Copy .env.sample to .env in Patient/RepeatMD.PatientFrontend and set Algolia credentials from 1Password.",
    );
  }

  cachedClient = withCatalogSearchWhen(
    withDatabaseFallback(algoliasearch(appId, searchApiKey), loadFallbackRecords, (error) =>
      datadogRum.addError(error, { source: "algoliaSearchClient", recovery: "database-fallback" }),
    ),
    isInHouseCatalogSearchOn,
  );
  return cachedClient;
}

export function getAlgoliaSearchIndexEnvName(): string {
  return import.meta.env.REACT_APP_ALGOLIA_ENV || DATABASE_CATALOG_ENV_NAME;
}

export function getAlgoliaSearchIndexName(suffix = "search"): string {
  return `${getAlgoliaSearchIndexEnvName()}_${suffix}`;
}
